import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { 
    Container, Row, Col, Card, CardBody, CardHeader, 
    Table, Input, Label, Badge, Button, InputGroup, 
    InputGroupText 
} from "reactstrap";
import { 
    Search, Calendar, ChevronLeft, ChevronRight, 
    MessageSquare, Activity, Clock
} from "lucide-react"; 
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import AdminHeader from "./Header";

const LIMIT = 10;

const AdminActivityLog = () => {
    const dispatch = useDispatch();

    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [page, setPage] = useState(1);

    const fetchLogs = useCallback(async () => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                limit: LIMIT,
                offset: (page - 1) * LIMIT,
                search: search,
                from: from,
                to: to
            }).toString();

            const res = await Handler({
                method: "get",
                url: `/admin/activity_log.php?${queryParams}`
            });

            if (res?.success) setLogs(res.data.list || []);
        } catch (err) {
            Handler(err);
        } finally {
            dispatch(loadingFalse());
        }
    }, [page, search, from, to, dispatch]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => fetchLogs(), 500);
        return () => clearTimeout(delayDebounceFn);
    }, [fetchLogs]);

    return (
        <div className="bg-soft-blue min-vh-100">
            <AdminHeader />
            <Container py="4" className="mt-4 pb-5">
                
                {/* PAGE TITLE */}
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                        <div className="icon-box-blue me-3">
                            <Activity size={22} />
                        </div>
                        <h3 className="fw-bold text-navy mb-0">Activity Logs</h3>
                    </div>
                    <Badge color="primary" pill className="px-3 py-2 shadow-sm">
                        Total Records: {logs.length}
                    </Badge>
                </div>

                {/* FILTERS SECTION */}
                <Card className="border-0 shadow-sm mb-4">
                    <CardHeader className="bg-white border-bottom-0 pt-3">
                        <div className="d-flex align-items-center text-primary fw-bold small text-uppercase">
                            <Clock size={14} className="me-2" /> Filter Activity Period
                        </div>
                    </CardHeader>
                    <CardBody className="pt-0">
                        <Row className="g-3">
                            <Col md={4}>
                                <Label className="small fw-bold text-muted">Search Business</Label>
                                <InputGroup size="sm" className="custom-input-group">
                                    <InputGroupText className="bg-light border-end-0 text-muted">
                                        <Search size={14} />
                                    </InputGroupText>
                                    <Input 
                                        placeholder="Name or ID..." 
                                        className="bg-light border-start-0 shadow-none"
                                        value={search} 
                                        onChange={e => { setPage(1); setSearch(e.target.value); }} 
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={4}>
                                <Label className="small fw-bold text-muted">From Date</Label>
                                <InputGroup size="sm" className="custom-input-group">
                                    <InputGroupText className="bg-light border-end-0 text-muted">
                                        <Calendar size={14} />
                                    </InputGroupText>
                                    <Input 
                                        type="date" 
                                        className="bg-light border-start-0 shadow-none"
                                        value={from} 
                                        onChange={e => { setPage(1); setFrom(e.target.value); }} 
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={4}>
                                <Label className="small fw-bold text-muted">To Date</Label>
                                <InputGroup size="sm" className="custom-input-group">
                                    <InputGroupText className="bg-light border-end-0 text-muted">
                                        <Calendar size={14} />
                                    </InputGroupText>
                                    <Input 
                                        type="date" 
                                        className="bg-light border-start-0 shadow-none"
                                        value={to} 
                                        onChange={e => { setPage(1); setTo(e.target.value); }} 
                                    />
                                </InputGroup>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* DATA TABLE */}
                <Card className="border-0 shadow-sm overflow-hidden">
                    <div className="table-responsive">
                        <Table hover className="mb-0 align-middle">
                            <thead className="bg-navy text-white">
                                <tr className="small text-uppercase">
                                    <th className="ps-4 py-3 border-0">Log ID</th>
                                    <th className="border-0">Business Activity</th>
                                    <th className="text-center border-0">Volume</th>
                                    <th className="border-0">Recent Activity</th>
                                    <th className="pe-4 border-0">Timestamp</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {logs?.length > 0 ? logs.map(log => (
                                    <tr key={log.chat_id} className="border-bottom">
                                        <td className="ps-4 text-primary fw-bold small">#{log.chat_id}</td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                <span className="fw-bold text-navy">{log.business_one.business_name}</span>
                                                <span className="text-muted x-small">
                                                    <span className="text-primary fw-medium">Connected to:</span> {log.business_two.business_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            {/* Changed badge class and icon styling here */}
                                            <Badge color="soft-success" className="rounded-pill px-3 py-2 text-success">
                                                <MessageSquare size={12} className="me-1 text-success" />
                                                {log.message_count}
                                            </Badge>
                                        </td>
                                        <td>
                                            <div className="text-dark small fw-medium">{log.last_message_at}</div>
                                        </td>
                                        <td className="pe-4">
                                            <div className="text-muted x-small fst-italic">{log.chat_created_at}</div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="5" className="text-center py-5">
                                            <div className="text-muted mb-2">No activity records found</div>
                                            <Button size="sm" color="outline-primary" onClick={() => { setSearch(""); setFrom(""); setTo(""); }}>Reset Filters</Button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card>

                {/* PAGINATION */}
                <div className="d-flex justify-content-between align-items-center mt-4 bg-white p-3 rounded shadow-sm border-start border-primary border-4">
                    <span className="text-muted small">
                        Showing results for page <b className="text-primary">{page}</b>
                    </span>
                    <div className="d-flex gap-2">
                        <Button 
                            outline 
                            color="primary" 
                            size="sm"
                            disabled={page === 1} 
                            onClick={() => setPage(p => p - 1)}
                            className="px-3"
                        >
                            <ChevronLeft size={16} /> Previous
                        </Button>
                        <Button 
                            color="primary" 
                            size="sm"
                            onClick={() => setPage(p => p + 1)}
                            className="px-3 shadow-sm"
                        >
                            Next <ChevronRight size={16} />
                        </Button>
                    </div>
                </div>
            </Container>

            {/* UPGRADED STYLES */}
            <style>{`
                .bg-soft-blue { background-color: #f0f4f8; }
                .text-navy { color: #1a365d; }
                .bg-navy { background-color: #1e3a8a; }
                .x-small { font-size: 0.72rem; }
                
                .icon-box-blue {
                    background: #1e3a8a;
                    color: white;
                    padding: 8px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .custom-input-group .form-control:focus {
                    background-color: #fff;
                    border-color: #1e3a8a;
                }

                /* Updated Message Square Styles */
                .badge-soft-success {
                    background-color: #d1fae5; /* Light Green */
                    color: #059669; /* Darker Green text */
                    border: 1px solid #a7f3d0;
                }

                .table-responsive { border-radius: 12px; }
                .table thead th { font-size: 0.75rem; letter-spacing: 0.05rem; }
                .table tbody tr:hover { background-color: #f8fafc !important; }
                
                .card { border-radius: 15px; }
                .shadow-sm { box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.05) !important; }
            `}</style>
        </div>
    );
};

export default AdminActivityLog;